
import React, { PropsWithChildren, useEffect, useState } from 'react';
import axios from 'axios';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './VideoPlaylist.css';
import { useApiTokenContext } from '../../providers/ApiTokenProvider/ApiTokenProvider';
import { TextLine, VideoPlaylistCL } from '../../importCL';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import VideoPlaylistCarousel from './CarouselVariant/VideoPlaylistCarouselVariant';
import { IRecommendedVideos, IYoutubeResourceIdBean, YoutubePlaylistItem } from '../../utils/youtube-utils/YoutubeTypes/YoutubeTypes';
import { LinkWrapper } from '../LinkWrapper/LinkWrapper';

export const VideoPlaylistEditConfig = {
    emptyLabel: 'Video Playlist',
    isEmpty: (props: PropsWithChildren<IVideoPlaylistProps>) => {
        return !props || !props.videoPlaylistLink;
    },
};

export interface IVideoPlaylistProps extends MappedComponentProperties {
    heading?: string;
    subHeading?: string;
    videoPlaylistLink?: string;
    recommendedVideos?: boolean;
    recommendedVideosList?: IRecommendedVideos[];
    videoDisclaimer?: string;
    carouselVariant?: boolean;
    centeredHeader?: boolean;
    youtubeBeans?: IYoutubeResourceIdBean[];
}

const VideoPlaylist = (props: PropsWithChildren<IVideoPlaylistProps>): JSX.Element => {
    const [playlistData, setPlaylistData] = useState<YoutubePlaylistItem[]>([]);
    const { getTokenString } = useApiTokenContext();
    const GOOGLE_API_DOMAIN = 'https://www.googleapis.com/youtube/v3/videos?';
    const GOOGLE_API_KEY = '&key=' + getTokenString('youtubeKey');
    const GOOGLE_API_PART_PARAM = '&part=snippet,';
    const VIDEO_PARAM = 'contentDetails&id=';

    const filterOutVideos = (videos: YoutubePlaylistItem[]) => {
        videos.forEach((video: YoutubePlaylistItem, i: number) => {
            if (
                video.snippet.description === 'This video is private.' ||
                video.snippet.description === 'This video is unavailable.'
            ) {
                videos.splice(i, 1);
            }
        });

        const reversedRecommendedVideosList = props.recommendedVideosList ? props.recommendedVideosList.reverse() : props.recommendedVideosList;

        videos = reorderPlaylist(videos, reversedRecommendedVideosList);
        return videos;
    };

    const removeVideoFromPlaylist = (videoId: string, videos: any[]) => {
        videos.forEach((video, index) => {
            if (video !== null && video.snippet.resourceId.videoId === videoId) {
                videos.splice(index, 1);
            }
        });
        return videos;
    };

    const findVideoFromPlaylist = (videoId: string, videos: YoutubePlaylistItem[]) => {
        let foundVideo = null;
        videos.forEach(video => {
            if (video !== null && video.snippet.resourceId.videoId === videoId) {
                foundVideo = video;
            }
        });
        return foundVideo;
    };

    const reorderPlaylist = (playlistVideos: YoutubePlaylistItem[], recommendedVideosList?: IRecommendedVideos[]) => {
        let reorderedPlaylist: any[] = [];
        if (!props.recommendedVideos || !recommendedVideosList) {
            reorderedPlaylist = playlistVideos;
        }
        if (recommendedVideosList && recommendedVideosList.length > 0) {
            [...recommendedVideosList].reverse().forEach((item) => {
                const video = findVideoFromPlaylist(item.videoId, playlistVideos);
                if (video !== null) {
                    if (!props.recommendedVideos) {
                        removeVideoFromPlaylist(item.videoId, reorderedPlaylist);
                    }
                    reorderedPlaylist.unshift(video);
                }
            });
        }
        return reorderedPlaylist;
    };

    useEffect(() => {
        if (props.carouselVariant) {
            return;
        }
        if (!props.videoPlaylistLink || GOOGLE_API_KEY.includes('null')) {
            return;
        }
        axios.get(props.videoPlaylistLink + GOOGLE_API_KEY)
            .then((response) => {
                setPlaylistData(filterOutVideos(response.data.items));
            })
            .catch(() => {
                console.error('Could not retrieve playlist data');
                setPlaylistData([]);
            });
    }, [props.videoPlaylistLink, props.recommendedVideos, props.recommendedVideosList, GOOGLE_API_KEY]);

    useEffect(() => {
        if (props.carouselVariant) {
            return;
        }
        if (playlistData[0]) {
            const video = playlistData[0];
            axios.get(GOOGLE_API_DOMAIN + GOOGLE_API_PART_PARAM + VIDEO_PARAM + video.snippet.resourceId.videoId + GOOGLE_API_KEY)
                .then((response) => {
                    if (response.data.items[0]) {
                        const videoData = response.data.items[0];
                        const videoStructuredData = {
                            '@context': 'https://schema.org',
                            '@type': 'VideoObject',
                            'name': `${videoData.snippet.title}`,
                            'description': `${videoData.snippet.description}`,
                            'thumbnailUrl': `${videoData.snippet.thumbnails.default.url}`,
                            'duration': `${videoData.contentDetails.duration}`,
                            'contentUrl': `https://www.youtube.com/watch?v=${videoData.id}`,
                            'embedUrl': `https://www.youtube.com/embed/${videoData.id}`,
                            'uploadDate': `${videoData.snippet.publishedAt}`,
                            'potentialAction': {
                                '@type': 'SeekToAction',
                                'target': `https://www.youtube.com/watch?v=${videoData.id}&t={seek_to_second_number}`,
                                'startOffset-input': 'required name=seek_to_second_number',
                            },
                        };

                        const schemaCode = document.createElement('script');
                        schemaCode.type = 'application/ld+json';
                        schemaCode.innerHTML = JSON.stringify(videoStructuredData);
                        document.head.append(schemaCode);

                    }
                })
                .catch(() => {
                    console.error('Could not retrieve video data');
                });
        }
    }, [playlistData]);

    if (props.carouselVariant) {
        return (
            <VideoPlaylistCarousel
                {...props}
                />)
    }

    return (
        <div>
            { props.heading &&
                <TextLine 
                    variant='author'
                    tag='h2'
                    subHeading={props.subHeading ? transformHTMLParserLinks(props.subHeading) : ''}
                    className='mb-6'
                >
                    {transformHTMLParserLinks(props.heading)}
                </TextLine>
            }
            <div id='video-playlist' className='Playlist'>
                {
                    playlistData && 
                    playlistData.length > 0 &&
                    <>
                        <VideoPlaylistCL 
                            data={playlistData} 
                            videoDisclaimer={
                                props.videoDisclaimer || props.videoDisclaimer === null ? transformHTMLParserLinks(props.videoDisclaimer) :
                                    <p className='VideoPlaylist ytdisclaimer'>Credit One Bank uses YouTube API services and by watching the video, you are agreeing to the YouTube Terms of Service (<LinkWrapper target={'_self'} href={'https://youtube.com/terms'}>https://youtube.com/terms</LinkWrapper>) and Google Privacy Policy (<LinkWrapper target={'_self'} href={'https://www.google.com/policies/privacy'}>https://www.google.com/policies/privacy</LinkWrapper>).</p>
                            }
                        />
                        
                    </>
                }
            </div>
        </div>
    );
};

export default VideoPlaylist;
